/* ============================================================================
   Wishlist — no-backend, localStorage-based.
   - Stores saved products as { id, handle } under a single key.
   - Syncs every [data-wish] heart button (saved state) across the page.
   - Keeps header [data-wishlist-count] badge in sync.
   - Renders a slide-out drawer (#WishlistDrawer) with the saved products,
     fetching fresh product data from /products/<handle>.js on demand.
   Exposes window.Wishlist.
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'playasoles:wishlist';
  var listeners = [];
  var productCache = {}; // handle -> product json (or null while pending)

  /* ---------- storage ---------- */
  function read() {
    var raw;
    try { raw = JSON.parse(localStorage.getItem(KEY)); } catch (e) { raw = null; }
    if (!Array.isArray(raw)) return [];
    // Migrate legacy format (array of id strings) -> array of {id, handle}.
    return raw
      .map(function (entry) {
        if (entry && typeof entry === 'object') {
          return { id: String(entry.id), handle: entry.handle || null };
        }
        return { id: String(entry), handle: null };
      })
      .filter(function (e) { return e.id; });
  }

  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    notify(list);
  }

  function notify(list) {
    listeners.forEach(function (fn) { try { fn(list); } catch (e) {} });
  }

  /* ---------- public api ---------- */
  var Wishlist = {
    all: function () { return read(); },
    count: function () { return read().length; },
    has: function (id) {
      id = String(id);
      return read().some(function (e) { return e.id === id; });
    },
    toggle: function (item) {
      var id = String(item.id);
      var list = read();
      var at = -1;
      for (var i = 0; i < list.length; i++) { if (list[i].id === id) { at = i; break; } }
      if (at === -1) {
        list.push({ id: id, handle: item.handle || null });
      } else {
        list.splice(at, 1);
      }
      write(list);
      return at === -1; // true if now saved
    },
    remove: function (id) {
      id = String(id);
      write(read().filter(function (e) { return e.id !== id; }));
    },
    subscribe: function (fn) { listeners.push(fn); return fn; }
  };
  window.Wishlist = Wishlist;

  /* ---------- money formatting (Shopify standard) ---------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var fmt = format || 'Rs. {{amount}}';

    function fmtNum(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }

    var value = '';
    var match = fmt.match(placeholder);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = fmtNum(cents, 2); break;
      case 'amount_no_decimals': value = fmtNum(cents, 0); break;
      case 'amount_with_comma_separator': value = fmtNum(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmtNum(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = fmtNum(cents, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fmtNum(cents, 0, ' ', ''); break;
      default: value = fmtNum(cents, 2);
    }
    return fmt.replace(placeholder, value);
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ---------- heart buttons + count badge sync ---------- */
  function syncHearts(list) {
    var saved = {};
    list.forEach(function (e) { saved[e.id] = true; });
    document.querySelectorAll('[data-wish]').forEach(function (btn) {
      var on = !!saved[String(btn.getAttribute('data-wish'))];
      btn.classList.toggle('is-saved', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      var n = list.length;
      el.textContent = n;
      el.toggleAttribute('hidden', n === 0);
    });
  }

  /* ---------- drawer ---------- */
  function getDrawer() { return document.getElementById('WishlistDrawer'); }

  function openDrawer() {
    var d = getDrawer();
    if (!d) return;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    renderDrawer();
  }

  function closeDrawer() {
    var d = getDrawer();
    if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle] !== undefined) return Promise.resolve(productCache[handle]);
    return fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { productCache[handle] = p; return p; })
      .catch(function () { productCache[handle] = null; return null; });
  }

  function renderDrawer() {
    var d = getDrawer();
    if (!d) return;
    var listEl = d.querySelector('[data-wishlist-list]');
    var emptyEl = d.querySelector('[data-wishlist-empty]');
    var fmt = d.getAttribute('data-money-format') || 'Rs. {{amount}}';
    var entries = read();

    if (!entries.length) {
      if (listEl) listEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      return;
    }
    if (emptyEl) emptyEl.hidden = true;

    Promise.all(entries.map(function (e) {
      return fetchProduct(e.handle).then(function (p) { return { entry: e, product: p }; });
    })).then(function (rows) {
      // Drop entries whose product can't be loaded (deleted/unavailable handle).
      var html = rows.map(function (row) {
        var p = row.product;
        var id = row.entry.id;
        if (!p) return '';
        var img = p.featured_image || (p.images && p.images[0]) || '';
        var price = formatMoney(p.price, fmt);
        var singleVariant = p.variants && p.variants.length === 1;
        var firstVariant = p.variants && p.variants[0];
        var canAdd = singleVariant && firstVariant && firstVariant.available;
        var action = canAdd
          ? '<button class="wishlist-item__add" type="button" data-wishlist-add="' + firstVariant.id + '">Add to bag</button>'
          : '<a class="wishlist-item__add wishlist-item__add--link" href="' + p.url + '">View product</a>';
        return (
          '<li class="wishlist-item" data-wishlist-item="' + id + '">' +
            '<a class="wishlist-item__media" href="' + p.url + '">' +
              (img ? '<img src="' + sizedImage(img, 160) + '" alt="" loading="lazy">' : '') +
            '</a>' +
            '<div class="wishlist-item__info">' +
              '<a class="wishlist-item__title" href="' + p.url + '">' + p.title + '</a>' +
              '<span class="wishlist-item__price">' + price + '</span>' +
              '<div class="wishlist-item__actions">' +
                action +
                '<button class="wishlist-item__remove" type="button" data-wishlist-remove="' + id + '" aria-label="Remove from wishlist">Remove</button>' +
              '</div>' +
            '</div>' +
          '</li>'
        );
      }).join('');
      if (listEl) listEl.innerHTML = html;
    });
  }

  function addToBag(variantId, btn) {
    if (!variantId) return;
    var original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
      .then(function (res) {
        if (!res.ok) throw new Error('add failed');
        btn.textContent = 'Added ✓';
        // Take the shopper to the cart so the count/contents reflect the add
        // regardless of which cart UI the theme uses.
        window.location.href = '/cart';
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = original;
      });
  }

  /* ---------- global event wiring ---------- */
  document.addEventListener('click', function (ev) {
    var heart = ev.target.closest('[data-wish]');
    if (heart) {
      ev.preventDefault();
      Wishlist.toggle({ id: heart.getAttribute('data-wish'), handle: heart.getAttribute('data-wish-handle') });
      return;
    }
    if (ev.target.closest('[data-wishlist-open]')) {
      ev.preventDefault();
      openDrawer();
      return;
    }
    if (ev.target.closest('[data-wishlist-close]')) {
      ev.preventDefault();
      closeDrawer();
      return;
    }
    var rm = ev.target.closest('[data-wishlist-remove]');
    if (rm) {
      ev.preventDefault();
      Wishlist.remove(rm.getAttribute('data-wishlist-remove'));
      return;
    }
    var add = ev.target.closest('[data-wishlist-add]');
    if (add) {
      ev.preventDefault();
      addToBag(add.getAttribute('data-wishlist-add'), add);
      return;
    }
  });

  document.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape') closeDrawer();
  });

  // Re-render drawer contents whenever the list changes while it is open.
  Wishlist.subscribe(function (list) {
    syncHearts(list);
    var d = getDrawer();
    if (d && d.classList.contains('is-open')) renderDrawer();
  });

  // Keep multiple tabs in sync.
  window.addEventListener('storage', function (ev) {
    if (ev.key === KEY) syncHearts(read());
  });

  // Initial paint.
  function init() { syncHearts(read()); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
